import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  GRADIENT_PRESETS,
  MIN_IMAGE_OVERLAY,
  type SurfaceFill,
  type SurfaceKind,
} from '@/entities/appearance';
import { useUpdateWorkspace } from '@/entities/collab';
import { usePaletteOptions } from '@/entities/database';
import { uploadHeaderImage, type Workspace } from '@/entities/workspace';
import { FocalPoint, GradientEditor, useBackgroundImageDraft } from '@/features/appearance-editor';
import { FormError } from '@/shared/forms';
import { Button, Dialog, Icons, PalettePicker } from '@/shared/ui';

import styles from './WorkspaceAppearanceDialog.module.css';
import { WorkspaceBanner } from './WorkspaceBanner';
import { WorkspaceInfoPreview } from './WorkspaceInfo';

type Part = 'header' | 'description';

const NONE: SurfaceFill = { kind: 'none' };
const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);
/** A fill with nothing in it is stored as absent. */
const stored = (fill: SurfaceFill): SurfaceFill | null => (fill.kind === 'none' ? null : fill);

function kindFill(kind: SurfaceKind, color: string, previous?: SurfaceFill): SurfaceFill {
  if (kind === 'image') {
    return {
      kind,
      position_x: previous?.position_x ?? 0.5,
      position_y: previous?.position_y ?? 0.5,
      overlay: previous?.overlay ?? 0.35,
    };
  }
  if (kind === 'color') return { kind, color };
  if (kind === 'gradient') {
    const preset = GRADIENT_PRESETS[0];
    return {
      kind,
      gradient: preset ? { ...preset } : { start: color, end: '#ffffff', angle: 135 },
    };
  }
  return NONE;
}

/**
 * Appearance of the workspace: two independent surfaces - the header (name and actions) and the
 * information area under it. Each is the theme default, a solid color or a gradient; the header
 * can also be a photo (focal point + scrim). Colors come from the shared palette picker (with the
 * custom #rrggbb field), the gradient from the shared gradient editor, the focal point from the
 * board background editor. The preview is the real banner drawn from the draft. Nothing reaches
 * the server until Save (the picked image is uploaded then), Cancel discards the draft. A header
 * color stored the older way (``workspace.color``) is shown as a solid fill and migrated only if
 * it is touched.
 */
export function WorkspaceAppearanceDialog({
  workspace,
  onClose,
}: {
  workspace: Workspace;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateWorkspace(workspace.id);
  const options = usePaletteOptions();
  const legacy = !workspace.appearance?.header && workspace.color !== null;
  const initial: Record<Part, SurfaceFill> = {
    header:
      workspace.appearance?.header ??
      (legacy && workspace.color ? { kind: 'color', color: workspace.color } : NONE),
    description: workspace.appearance?.description ?? NONE,
  };
  const [part, setPart] = useState<Part>('header');
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const image = useBackgroundImageDraft(workspace.header_image_url ?? null);
  const picker = useRef<HTMLInputElement>(null);

  const imageTouched = image.file !== null || image.removed;
  const dirty = !same(draft, initial) || imageTouched;
  const headerTouched = !same(draft.header, initial.header);
  // an image header needs a picture (the stored one, or a newly chosen file)
  const incomplete = draft.header.kind === 'image' && !image.previewUrl;
  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      if (draft.header.kind === 'image' && image.file) {
        await uploadHeaderImage(workspace.id, image.file);
      }
      const header = stored(draft.header);
      const description = stored(draft.description);
      // an untouched legacy tint stays as it is (header stays absent, `color` is not cleared)
      const keepLegacy = legacy && !headerTouched;
      const appearance =
        (keepLegacy ? null : header) || description
          ? { v: 1 as const, header: keepLegacy ? null : header, description }
          : null;
      await update.mutateAsync({
        appearance,
        ...(headerTouched && legacy && { color: null }),
      });
      onClose();
    } catch (failure) {
      setError(failure);
    } finally {
      setSaving(false);
    }
  };

  const fill = draft[part];
  const fallback = options[0]?.value ?? '#2860cc';
  const change = (next: SurfaceFill) => {
    setDraft({ ...draft, [part]: next });
  };
  const kinds: readonly SurfaceKind[] =
    part === 'header' ? ['none', 'color', 'gradient', 'image'] : ['none', 'color', 'gradient'];

  return (
    <Dialog title={t('workspaceHeader.title')} onClose={onClose} size="lg">
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          // a form of a dialog opened from inside (the palette editor) bubbles through the portal
          if (event.target === event.currentTarget && !incomplete) void save();
        }}
      >
        <div
          className={styles.preview}
          role="img"
          aria-label={t('workspaceHeader.preview')}
          data-testid="workspace-appearance-preview"
        >
          <div inert>
            <WorkspaceBanner
              preview
              title={<span className={styles.previewTitle}>{workspace.name}</span>}
              chips={[
                t(workspace.workspace_type === 'team' ? 'home.scopeTeam' : 'home.scopePersonal'),
              ]}
              primary={<Button variant="primary">{t('home.create')}</Button>}
              tools={<Icons.calendar />}
              appearance={{ v: 1, header: draft.header, description: draft.description }}
              imageUrl={image.previewUrl}
            >
              <WorkspaceInfoPreview
                text={
                  workspace.description?.trim()
                    ? workspace.description
                    : t('workspaceHeader.sample')
                }
                appearance={{ v: 1, header: draft.header, description: draft.description }}
              />
            </WorkspaceBanner>
          </div>
        </div>

        <div className={styles.tabs} role="tablist" aria-label={t('workspaceHeader.parts')}>
          {(['header', 'description'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              className={`${styles.tab} ${part === value ? styles.tabOn : ''}`}
              aria-selected={part === value}
              onClick={() => {
                setPart(value);
              }}
            >
              {t(`workspaceHeader.part.${value}`)}
            </button>
          ))}
        </div>
        <p className={styles.hint}>{t(`workspaceHeader.hint.${part}`)}</p>

        <div className={styles.kinds} role="group" aria-label={t('workspaceHeader.fill')}>
          {kinds.map((kind) => (
            <button
              key={kind}
              type="button"
              className={styles.kind}
              aria-pressed={fill.kind === kind}
              onClick={() => {
                if (fill.kind !== kind) change(kindFill(kind, fill.color ?? fallback, fill));
              }}
            >
              {t(`workspaceHeader.kind.${kind}`)}
            </button>
          ))}
        </div>

        {fill.kind === 'color' && (
          <PalettePicker
            label={t(`workspaceHeader.color.${part}`)}
            value={fill.color ?? null}
            options={options}
            noneLabel={t('databases.palette.none')}
            custom
            workspaceId={workspace.id}
            onPick={(color) => {
              change(color ? { kind: 'color', color } : NONE);
            }}
          />
        )}
        {fill.kind === 'gradient' && fill.gradient && (
          <GradientEditor
            value={fill.gradient}
            onChange={(gradient) => {
              change({ kind: 'gradient', gradient });
            }}
          />
        )}
        {part === 'header' && fill.kind === 'image' && (
          <div className={styles.image}>
            <div className={styles.imageButtons}>
              <Button
                onClick={() => {
                  picker.current?.click();
                }}
              >
                {t(
                  image.previewUrl
                    ? 'appearance.background.replaceImage'
                    : 'appearance.background.chooseImage',
                )}
              </Button>
              {image.previewUrl && (
                <Button variant="ghost" onClick={image.remove}>
                  {t('appearance.background.removeImage')}
                </Button>
              )}
              <input
                ref={picker}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="visually-hidden"
                aria-label={t('appearance.background.chooseImage')}
                data-testid="workspace-header-image-input"
                onChange={(event) => {
                  image.setFile(event.target.files?.[0] ?? null);
                  event.target.value = '';
                }}
              />
            </div>
            <p className={styles.hint}>{t('appearance.background.imageHint')}</p>
            {image.previewUrl ? (
              <>
                <FocalPoint
                  src={image.previewUrl}
                  x={fill.position_x ?? 0.5}
                  y={fill.position_y ?? 0.5}
                  onChange={(x, y) => {
                    change({ ...fill, position_x: x, position_y: y });
                  }}
                />
                <label className={styles.slider}>
                  <span>
                    {t('appearance.background.overlay', {
                      percent: Math.round((fill.overlay ?? 0.35) * 100),
                    })}
                  </span>
                  <input
                    type="range"
                    min={Math.round(MIN_IMAGE_OVERLAY * 100)}
                    max={80}
                    step={5}
                    value={Math.round(Math.max(fill.overlay ?? 0.35, MIN_IMAGE_OVERLAY) * 100)}
                    onChange={(event) => {
                      change({ ...fill, overlay: Number(event.target.value) / 100 });
                    }}
                  />
                </label>
              </>
            ) : (
              <p className={styles.hint}>{t('appearance.background.noImage')}</p>
            )}
          </div>
        )}

        <FormError error={error} />
        <div className={styles.actions}>
          <Button type="submit" variant="primary" disabled={!dirty || incomplete || saving}>
            {t('common.save')}
          </Button>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <span className={styles.spacer} />
          <Button
            variant="ghost"
            onClick={() => {
              setDraft({ header: NONE, description: NONE });
              if (image.previewUrl) image.remove();
            }}
          >
            {t('workspaceHeader.reset')}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
